import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Car, 
  LogOut, 
  FileText, 
  User, 
  Plus,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  RefreshCw,
  Edit,
  Save,
  X,
  Download,
  Hash,
  Truck,
  MessageSquare
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dossiers, setDossiers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("dossiers");
  const [editDossierInfo, setEditDossierInfo] = useState(false);
  const [editedClientInfo, setEditedClientInfo] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [dossiersRes, profileRes] = await Promise.all([
        axios.get(`${API}/client/dossiers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/client/profile`, { headers: getAuthHeaders() })
      ]);
      
      setDossiers(dossiersRes.data);
      setProfile(profileRes.data);
      setEditedProfile(profileRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expirée");
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_data');
        navigate('/connexion');
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/connexion');
      return;
    }
    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_data');
    toast.success("Déconnexion réussie");
    navigate('/');
  };

  const saveProfile = async () => {
    try {
      await axios.put(`${API}/client/profile`, editedProfile, { headers: getAuthHeaders() });
      setProfile(editedProfile);
      setEditProfile(false);
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const fetchDossierDetail = async (dossierId) => {
    try {
      const response = await axios.get(`${API}/client/dossiers/${dossierId}`, { headers: getAuthHeaders() });
      setSelectedDossier(response.data);
      setEditedClientInfo(response.data.client);
      setEditDossierInfo(false);
    } catch (error) {
      toast.error("Erreur lors du chargement du dossier");
    }
  };

  const saveDossierClientInfo = async () => {
    if (!selectedDossier || !editedClientInfo) return;
    
    try {
      await axios.put(
        `${API}/client/dossiers/${selectedDossier.id}`,
        editedClientInfo,
        { headers: getAuthHeaders() }
      );
      
      setSelectedDossier(prev => ({ ...prev, client: editedClientInfo }));
      setEditDossierInfo(false);
      toast.success("Informations mises à jour");
      fetchData(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const canEditDossier = (status) => {
    return ['en_attente', 'documents_manquants', 'payé'].includes(status);
  };

  const downloadCerfa = async (dossierId, cerfaType) => {
    try {
      const response = await axios.get(
        `${API}/dossiers/${dossierId}/cerfa/${cerfaType}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cerfa_${cerfaType}_${dossierId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`CERFA ${cerfaType} téléchargé`);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'en_attente': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'En attente' },
      'payé': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CreditCard, label: 'Payé' },
      'en_cours': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: RefreshCw, label: 'En cours de traitement' },
      'documents_manquants': { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle, label: 'Documents manquants' },
      'terminé': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Terminé' },
      'rejeté': { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle, label: 'Rejeté' }
    };
    
    const cfg = config[status] || config['en_attente'];
    const Icon = cfg.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#000091] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#000091] text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-[#000091]" />
                </div>
                <div>
                  <h1 className="font-bold">Mon Espace</h1>
                  <p className="text-xs text-white/70">Carte Grise Étrangère</p>
                </div>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-white/80 hidden sm:block">
                {profile?.prenom} {profile?.nom}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="dossiers" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Mes dossiers
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" />
              Mon profil
            </TabsTrigger>
          </TabsList>

          {/* Dossiers Tab */}
          <TabsContent value="dossiers" className="space-y-6">
            {/* New dossier button */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-[#0F172A]">Mes dossiers ({dossiers.length})</h2>
              <Button 
                onClick={() => navigate('/demande')}
                className="bg-[#000091] hover:bg-[#000091]/90"
                data-testid="new-dossier-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle demande
              </Button>
            </div>

            {dossiers.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Aucun dossier</h3>
                  <p className="text-slate-500 mb-6">Vous n'avez pas encore de demande d'immatriculation</p>
                  <Button 
                    onClick={() => navigate('/demande')}
                    className="bg-[#000091] hover:bg-[#000091]/90"
                  >
                    Commencer une demande
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {dossiers.map((dossier) => (
                  <Card 
                    key={dossier.id} 
                    className="border-slate-200 hover:border-[#000091]/30 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => fetchDossierDetail(dossier.id)}
                    data-testid={`dossier-card-${dossier.id.slice(0, 8)}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#000091]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Car className="w-6 h-6 text-[#000091]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-[#0F172A]">
                                {dossier.vehicle?.marque} {dossier.vehicle?.modele}
                              </h3>
                              {getStatusBadge(dossier.status)}
                            </div>
                            <p className="text-sm text-[#000091] font-semibold mb-1">
                              {dossier.numero_dossier || `Réf: ${dossier.id.slice(0, 8).toUpperCase()}`}
                            </p>
                            <p className="text-xs text-slate-400">
                              Créé le {new Date(dossier.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {dossier.ww_provisoire && (
                            <div className="text-right">
                              <p className="text-xs text-slate-500">WW Provisoire</p>
                              <p className="font-mono font-semibold text-[#000091]">{dossier.ww_provisoire}</p>
                            </div>
                          )}
                          {dossier.immatriculation_definitive && (
                            <div className="text-right">
                              <p className="text-xs text-slate-500">Immat. définitive</p>
                              <p className="font-mono font-semibold text-green-600">{dossier.immatriculation_definitive}</p>
                            </div>
                          )}
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-[#0F172A]">
                    <User className="w-5 h-5 text-[#000091]" />
                    Mes informations
                  </CardTitle>
                  {!editProfile ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditProfile(true)}
                      className="border-[#000091] text-[#000091]"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setEditProfile(false);
                          setEditedProfile(profile);
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Annuler
                      </Button>
                      <Button 
                        size="sm"
                        onClick={saveProfile}
                        className="bg-[#000091] hover:bg-[#000091]/90"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {editProfile ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom</Label>
                      <Input
                        value={editedProfile?.prenom || ''}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, prenom: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom</Label>
                      <Input
                        value={editedProfile?.nom || ''}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, nom: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={editedProfile?.telephone || ''}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, telephone: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input value={profile?.email || ''} disabled className="bg-slate-50" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Adresse</Label>
                      <Input
                        value={editedProfile?.adresse || ''}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, adresse: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Code postal</Label>
                      <Input
                        value={editedProfile?.code_postal || ''}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, code_postal: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ville</Label>
                      <Input
                        value={editedProfile?.ville || ''}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, ville: e.target.value }))}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-slate-500">Nom complet</p>
                      <p className="font-medium text-[#0F172A]">{profile?.prenom} {profile?.nom}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-[#0F172A]">{profile?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Téléphone</p>
                      <p className="font-medium text-[#0F172A]">{profile?.telephone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Adresse</p>
                      <p className="font-medium text-[#0F172A]">
                        {profile?.adresse ? `${profile.adresse}, ${profile.code_postal} ${profile.ville}` : '-'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dossier Detail Modal */}
      <Dialog open={!!selectedDossier} onOpenChange={() => setSelectedDossier(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedDossier && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
                  <FileText className="w-5 h-5 text-[#000091]" />
                  {selectedDossier.numero_dossier || `Dossier #${selectedDossier.id.slice(0, 8).toUpperCase()}`}
                </DialogTitle>
                <DialogDescription>
                  {selectedDossier.vehicle?.marque} {selectedDossier.vehicle?.modele}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Dossier number highlight */}
                {selectedDossier.numero_dossier && (
                  <div className="p-4 bg-[#000091]/5 border border-[#000091]/20 rounded-lg">
                    <p className="text-sm text-slate-600">Numéro de dossier</p>
                    <p className="text-xl font-bold text-[#000091]" data-testid="modal-dossier-number">{selectedDossier.numero_dossier}</p>
                  </div>
                )}

                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Statut</span>
                  {getStatusBadge(selectedDossier.status)}
                </div>

                {/* Admin message */}
                {selectedDossier.admin_message && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Message de l'équipe</p>
                        <p className="text-sm text-blue-700 mt-1">{selectedDossier.admin_message}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Important numbers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <Hash className="w-6 h-6 text-[#000091] mx-auto mb-2" />
                      <p className="text-xs text-slate-500 mb-1">N° de dossier</p>
                      <p className="font-mono font-bold text-[#0F172A]">
                        {selectedDossier.id.slice(0, 8).toUpperCase()}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className={`border-slate-200 ${selectedDossier.ww_provisoire ? 'bg-blue-50 border-blue-200' : ''}`}>
                    <CardContent className="p-4 text-center">
                      <Truck className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 mb-1">WW Provisoire</p>
                      <p className="font-mono font-bold text-[#0F172A]">
                        {selectedDossier.ww_provisoire || '-'}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className={`border-slate-200 ${selectedDossier.immatriculation_definitive ? 'bg-green-50 border-green-200' : ''}`}>
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 mb-1">Immat. définitive</p>
                      <p className="font-mono font-bold text-[#0F172A]">
                        {selectedDossier.immatriculation_definitive || '-'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* CPI Number */}
                {selectedDossier.cpi_number && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-green-700 mb-1">Numéro CPI (Certificat Provisoire d'Immatriculation)</p>
                    <p className="font-mono font-bold text-lg text-green-800">{selectedDossier.cpi_number}</p>
                  </div>
                )}

                {/* Vehicle info */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-500">Véhicule</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Marque / Modèle</p>
                      <p className="font-medium">{selectedDossier.vehicle?.marque} {selectedDossier.vehicle?.modele}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">VIN</p>
                      <p className="font-medium font-mono text-xs">{selectedDossier.vehicle?.numero_serie}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Pays d'origine</p>
                      <p className="font-medium">{selectedDossier.vehicle?.pays_origine}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Énergie</p>
                      <p className="font-medium">{selectedDossier.vehicle?.energie}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Client info - Editable */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm text-slate-500">Vos informations</CardTitle>
                      {canEditDossier(selectedDossier.status) && !editDossierInfo && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setEditDossierInfo(true)}
                          className="border-[#000091] text-[#000091]"
                          data-testid="edit-client-info-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                      )}
                      {editDossierInfo && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setEditDossierInfo(false);
                              setEditedClientInfo(selectedDossier.client);
                            }}
                          >
                            <X className="w-4 h-4 mr-2" />
                            Annuler
                          </Button>
                          <Button 
                            size="sm"
                            onClick={saveDossierClientInfo}
                            className="bg-[#000091] hover:bg-[#000091]/90"
                            data-testid="save-client-info-btn"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Enregistrer
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm">
                    {editDossierInfo ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Civilité</Label>
                          <select 
                            className="w-full border border-slate-200 rounded-md p-2 text-sm"
                            value={editedClientInfo?.civilite || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, civilite: e.target.value }))}
                          >
                            <option value="M">Monsieur</option>
                            <option value="Mme">Madame</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Nom</Label>
                          <Input
                            value={editedClientInfo?.nom || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, nom: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Prénom</Label>
                          <Input
                            value={editedClientInfo?.prenom || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, prenom: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Téléphone</Label>
                          <Input
                            value={editedClientInfo?.telephone || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, telephone: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            value={editedClientInfo?.email || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, email: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date de naissance</Label>
                          <Input
                            type="date"
                            value={editedClientInfo?.date_naissance || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, date_naissance: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Lieu de naissance</Label>
                          <Input
                            value={editedClientInfo?.lieu_naissance || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, lieu_naissance: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Adresse</Label>
                          <Input
                            value={editedClientInfo?.adresse || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, adresse: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Code postal</Label>
                          <Input
                            value={editedClientInfo?.code_postal || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, code_postal: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Ville</Label>
                          <Input
                            value={editedClientInfo?.ville || ''}
                            onChange={(e) => setEditedClientInfo(prev => ({ ...prev, ville: e.target.value }))}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-slate-500">Nom complet</p>
                          <p className="font-medium">{selectedDossier.client?.civilite} {selectedDossier.client?.prenom} {selectedDossier.client?.nom}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Email</p>
                          <p className="font-medium">{selectedDossier.client?.email}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Téléphone</p>
                          <p className="font-medium">{selectedDossier.client?.telephone}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Date de naissance</p>
                          <p className="font-medium">{selectedDossier.client?.date_naissance}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-slate-500">Adresse</p>
                          <p className="font-medium">{selectedDossier.client?.adresse}, {selectedDossier.client?.code_postal} {selectedDossier.client?.ville}</p>
                        </div>
                      </div>
                    )}
                    {!canEditDossier(selectedDossier.status) && (
                      <p className="text-xs text-orange-600 mt-4 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        La modification n'est plus possible pour ce dossier (statut: {selectedDossier.status})
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Info message instead of downloads */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    Les formulaires CERFA sont traités par notre équipe et ne sont pas disponibles en téléchargement direct.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedDossier(null)}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
